import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useLanguage } from '../../context/LanguageContext';
import { formatCurrency, formatPercent, formatMonth } from '../../utils/formatCurrency';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, Cell, Legend
} from 'recharts';

const API = process.env.REACT_APP_API_URL || '/api';

// ── Styles (consistent with dark theme) ─────────────────────────
const cardStyle = { background: '#111827', border: '1px solid rgba(148,163,184,0.2)', padding: '1.25rem', textAlign: 'center' };
const labelStyle = { color: '#94A3B8', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.25rem' };
const valueStyle = { color: '#E8ECF0', fontSize: '1.5rem', fontFamily: "'Rajdhani', sans-serif", fontWeight: 600 };
const headStyle = { color: '#D42B2B', fontSize: '0.75rem', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '1rem', marginTop: '2rem' };
const thStyle = { color: '#94A3B8', fontSize: '0.7rem', textTransform: 'uppercase', padding: '0.5rem 0.75rem' };
const tdStyle = { color: '#E8ECF0', fontSize: '0.85rem', padding: '0.6rem 0.75rem' };
const rowBorder = { borderBottom: '1px solid rgba(148,163,184,0.08)' };
const STATUS_COLORS = { completed: '#22C55E', confirmed: '#3B82F6', in_progress: '#EAB308', pending: '#94A3B8', pending_payment: '#F97316', cancelled: '#EF4444' };

// ── Date preset helpers ─────────────────────────────────────────
function getPresetRange(key) {
  const today = new Date();
  const fmt = d => d.toISOString().slice(0, 10);
  const startOf = (y, m, d) => new Date(y, m, d);
  switch (key) {
    case 'today': return { start: fmt(today), end: fmt(today) };
    case '7d': { const s = new Date(today); s.setDate(s.getDate() - 6); return { start: fmt(s), end: fmt(today) }; }
    case 'month': return { start: fmt(startOf(today.getFullYear(), today.getMonth(), 1)), end: fmt(today) };
    case 'lastMonth': {
      const s = startOf(today.getFullYear(), today.getMonth() - 1, 1);
      const e = startOf(today.getFullYear(), today.getMonth(), 0);
      return { start: fmt(s), end: fmt(e) };
    }
    case 'quarter': {
      const qm = Math.floor(today.getMonth() / 3) * 3;
      return { start: fmt(startOf(today.getFullYear(), qm, 1)), end: fmt(today) };
    }
    case 'ytd': return { start: fmt(startOf(today.getFullYear(), 0, 1)), end: fmt(today) };
    case 'all': return { start: '', end: '' };
    default: return { start: '', end: '' };
  }
}

// ── Delta arrow component ───────────────────────────────────────
function Delta({ current, previous, invert }) {
  if (previous == null || previous === 0 || current == null) return null;
  const pct = ((current - previous) / Math.abs(previous)) * 100;
  if (Math.abs(pct) < 0.1) return null;
  const up = pct > 0;
  const good = invert ? !up : up;
  return (
    <span style={{ fontSize: '0.7rem', marginLeft: '0.35rem', color: good ? '#22C55E' : '#EF4444' }}>
      {up ? '\u25B2' : '\u25BC'} {Math.abs(pct).toFixed(1)}%
    </span>
  );
}

// ── Money display component ─────────────────────────────────────
function Money({ value, lang, estimate, style: extraStyle }) {
  if (value == null) return <span style={{ color: '#475569', ...extraStyle }}>—</span>;
  const isEst = estimate && Number(value) > 0;
  return (
    <span style={{ color: isEst ? '#64748B' : (Number(value) > 0 ? '#22C55E' : '#E8ECF0'), ...extraStyle }}>
      {formatCurrency(value, lang, { estimate: isEst })}
    </span>
  );
}

// ══════════════════════════════════════════════════════════════════
export default function ReportsTab() {
  const { t, language } = useLanguage();
  const r = t.admin?.reports || {};

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [dateRange, setDateRange] = useState(() => getPresetRange('month'));
  const [activePreset, setActivePreset] = useState('month');
  const [svcSort, setSvcSort] = useState('revenue');

  const presets = useMemo(() => [
    ['today', r.presetToday],
    ['7d', r.preset7d],
    ['month', r.presetMonth],
    ['lastMonth', r.presetLastMonth],
    ['quarter', r.presetQuarter],
    ['ytd', r.presetYtd],
    ['all', r.presetAll]
  ], [r]);

  const loadReport = useCallback(async (range) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      const src = range || dateRange;
      if (src.start) params.set('start', src.start);
      if (src.end) params.set('end', src.end);
      const res = await axios.get(`${API}/admin/reports/revenue?${params}`);
      setData(res.data);
    } catch (err) { /* ignore */ }
    setLoading(false);
  }, [dateRange]);

  useEffect(() => { loadReport(); }, []);

  const applyPreset = (key) => {
    const range = getPresetRange(key);
    setDateRange(range);
    setActivePreset(key);
    loadReport(range);
  };

  const applyCustom = (e) => {
    e.preventDefault();
    if (dateRange.start && dateRange.end && dateRange.start > dateRange.end) return;
    setActivePreset(null);
    loadReport();
  };

  // ── CSV Export ──────────────────────────────────────────────
  const exportCSV = () => {
    if (!data) return;
    const { summary: s, byService, byMonth, topCustomers } = data;
    const lines = [];
    lines.push(r.summaryTitle || 'Summary');
    lines.push(`${r.completedAppointments},${s.completed_appointments}`);
    lines.push(`${r.confirmedRevenue},${s.confirmed_revenue}`);
    lines.push(`${r.netRevenue},${s.net_revenue}`);
    lines.push(`${r.depositsCollected},${s.deposits_collected}`);
    lines.push(`${r.refundsIssued},${s.refunds_issued}`);
    lines.push('');
    lines.push(r.byService);
    lines.push([r.service, r.lineItems, r.appointments, r.confirmed, r.estimatedMid].join(','));
    byService.forEach(row => lines.push([row.service, row.line_items, row.appointments, row.confirmed, row.subtotal_mid].join(',')));
    lines.push('');
    lines.push(r.byMonth);
    lines.push([r.month, r.appointments, r.confirmed, r.estimatedRange].join(','));
    byMonth.forEach(row => lines.push([formatMonth(row.month, language), row.appointments, row.confirmed, `${row.revenue_low}-${row.revenue_high}`].join(',')));
    if (topCustomers?.length) {
      lines.push('');
      lines.push(r.topCustomersTitle);
      lines.push([r.customer, r.appointments, r.confirmed, r.lastVisit].join(','));
      topCustomers.forEach(c => lines.push([`${c.first_name} ${c.last_name}`, c.appointment_count, c.confirmed_revenue || c.estimated_revenue, c.last_visit].join(',')));
    }

    const blob = new Blob(['\uFEFF' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rapport-revenus-${dateRange.start || 'all'}-${dateRange.end || 'all'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── PDF Export ──────────────────────────────────────────────
  const exportPDF = async () => {
    if (!data) return;
    const { jsPDF } = await import('jspdf');
    await import('jspdf-autotable');
    const doc = new jsPDF();
    const { summary: s, byService, byMonth } = data;

    doc.setFontSize(16);
    doc.text('Mécanique Auto CJPA', 14, 20);
    doc.setFontSize(10);
    doc.text(r.summaryTitle || 'Revenue Report', 14, 28);
    if (dateRange.start || dateRange.end) {
      doc.text(`${dateRange.start || '...'} — ${dateRange.end || '...'}`, 14, 34);
    }

    let y = 42;
    doc.setFontSize(9);
    const kpis = [
      [r.completedAppointments, s.completed_appointments],
      [r.confirmedRevenue, formatCurrency(s.confirmed_revenue, language)],
      [r.netRevenue, formatCurrency(s.net_revenue, language)],
      [r.depositsCollected, formatCurrency(s.deposits_collected, language)],
      [r.completionRate, formatPercent(s.completion_rate)],
      [r.avgTicketConfirmed, s.avg_ticket_confirmed != null ? formatCurrency(s.avg_ticket_confirmed, language) : '—']
    ];
    kpis.forEach(([label, val]) => {
      doc.text(`${label}: ${val}`, 14, y);
      y += 6;
    });

    if (byService.length) {
      y += 4;
      doc.autoTable({
        startY: y,
        head: [[r.service, r.lineItems, r.confirmed, r.estimatedMid]],
        body: byService.map(row => [row.service, row.line_items, formatCurrency(row.confirmed, language), formatCurrency(row.subtotal_mid, language)]),
        theme: 'grid',
        headStyles: { fillColor: [212, 43, 43] },
        styles: { fontSize: 8 }
      });
      y = doc.lastAutoTable.finalY + 8;
    }

    if (byMonth.length) {
      doc.autoTable({
        startY: y,
        head: [[r.month, r.appointments, r.confirmed, r.estimatedRange]],
        body: byMonth.map(row => [formatMonth(row.month, language), row.appointments, formatCurrency(row.confirmed, language), `${formatCurrency(row.revenue_low, language)} – ${formatCurrency(row.revenue_high, language)}`]),
        theme: 'grid',
        headStyles: { fillColor: [212, 43, 43] },
        styles: { fontSize: 8 }
      });
    }

    doc.save(`rapport-revenus-${dateRange.start || 'all'}-${dateRange.end || 'all'}.pdf`);
  };

  // ── Loading / empty states ─────────────────────────────────
  if (loading && !data) return <div style={{ color: '#94A3B8', padding: '2rem', textAlign: 'center' }}>{t.common.loading}</div>;
  if (!data) return <div style={{ color: '#94A3B8', padding: '2rem', textAlign: 'center' }}>{r.noData}</div>;

  const { summary: s, prior, byService, byMonth, upcoming, upcomingByService, busiestTimes, topCustomers, statusDistribution, topVehicles, revenuePerHour, customerAcquisition } = data;

  // ── Derived data ───────────────────────────────────────────
  const totalMid = byService.reduce((acc, row) => acc + row.subtotal_mid, 0);

  const sortedServices = [...byService].sort((a, b) => {
    if (svcSort === 'count') return b.line_items - a.line_items;
    if (svcSort === 'name') return a.service.localeCompare(b.service, language);
    return b.subtotal_mid - a.subtotal_mid;
  });

  const monthlyTotal = byMonth.reduce((acc, row) => ({
    appointments: acc.appointments + row.appointments,
    confirmed: acc.confirmed + row.confirmed,
    revenue_low: acc.revenue_low + row.revenue_low,
    revenue_high: acc.revenue_high + row.revenue_high,
    revenue_mid: acc.revenue_mid + row.revenue_mid
  }), { appointments: 0, confirmed: 0, revenue_low: 0, revenue_high: 0, revenue_mid: 0 });

  // Chart data (reverse so ascending for chart)
  const monthChartData = [...byMonth].reverse().map(row => ({
    month: formatMonth(row.month, language),
    confirmed: row.confirmed,
    estimatedMid: row.revenue_mid,
    low: row.revenue_low,
    high: row.revenue_high
  }));

  const top5Services = byService.slice(0, 5).map(row => ({
    name: row.service.length > 20 ? row.service.slice(0, 18) + '…' : row.service,
    value: row.confirmed > 0 ? row.confirmed : row.subtotal_mid,
    isEstimate: row.confirmed === 0
  }));

  // Status distribution chart data
  const statusMonths = {};
  (statusDistribution || []).forEach(({ month, status, count }) => {
    if (!statusMonths[month]) statusMonths[month] = { month: formatMonth(month, language) };
    statusMonths[month][status] = (statusMonths[month][status] || 0) + count;
  });
  const statusChartData = Object.values(statusMonths).reverse();

  // Busiest times heatmap
  const heatmapMax = busiestTimes.reduce((m, t) => Math.max(m, t.count), 0) || 1;
  const dayLabels = language === 'fr'
    ? ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam']
    : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const heatmapData = {};
  busiestTimes.forEach(({ day_of_week, hour, count }) => {
    heatmapData[`${day_of_week}-${hour}`] = count;
  });
  const heatmapHours = [];
  for (let h = 7; h <= 19; h++) heatmapHours.push(h);

  return (
    <div>
      {/* ── Date presets + custom range ───────────────────────── */}
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        {presets.map(([key, label]) => (
          <button key={key} className={`btn btn-sm ${activePreset === key ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => applyPreset(key)} style={{ fontSize: '0.75rem' }}>
            {label}
          </button>
        ))}
      </div>
      <form onSubmit={applyCustom} aria-label={r.filterFormLabel} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <div>
          <label htmlFor="report-start" style={{ ...labelStyle, display: 'block' }}>{r.startDate}</label>
          <input id="report-start" type="date" className="form-control form-control-sm"
            value={dateRange.start} onChange={e => { setDateRange(prev => ({ ...prev, start: e.target.value })); setActivePreset(null); }}
            style={{ colorScheme: 'dark', width: '160px' }} />
        </div>
        <div>
          <label htmlFor="report-end" style={{ ...labelStyle, display: 'block' }}>{r.endDate}</label>
          <input id="report-end" type="date" className="form-control form-control-sm"
            value={dateRange.end} onChange={e => { setDateRange(prev => ({ ...prev, end: e.target.value })); setActivePreset(null); }}
            style={{ colorScheme: 'dark', width: '160px' }} />
        </div>
        <button type="submit" className="btn btn-primary btn-sm" disabled={loading}>{loading ? t.common.loading : r.applyFilter}</button>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={exportCSV} style={{ fontSize: '0.7rem' }}>{r.exportCSV}</button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={exportPDF} style={{ fontSize: '0.7rem' }}>{r.exportPDF}</button>
        </div>
      </form>

      {/* ── Loading overlay ───────────────────────────────────── */}
      {loading && <div style={{ color: '#D42B2B', textAlign: 'center', padding: '0.5rem', fontSize: '0.85rem' }}>{t.common.loading}</div>}

      {/* ── Zero-revenue banner ───────────────────────────────── */}
      {s.confirmed_revenue === 0 && s.completed_appointments > 0 && (
        <div style={{ background: 'rgba(234,179,8,0.1)', border: '1px solid rgba(234,179,8,0.3)', padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.8rem', color: '#EAB308' }}>
          {r.zeroBanner}
        </div>
      )}

      {/* ── Summary KPI cards ─────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
        <div style={cardStyle}>
          <div style={labelStyle}>{r.completedAppointments}</div>
          <div style={valueStyle}>{s.completed_appointments}<Delta current={s.completed_appointments} previous={prior?.completed_appointments} /></div>
        </div>
        <div style={cardStyle}>
          <div style={labelStyle}>{r.netRevenue}</div>
          <div style={{ ...valueStyle, color: s.net_revenue > 0 ? '#22C55E' : '#E8ECF0' }}>
            <Money value={s.net_revenue} lang={language} estimate={s.confirmed_revenue === 0 && s.net_revenue > 0} />
            <Delta current={s.net_revenue} previous={prior ? (prior.confirmed_revenue || prior.estimated_midpoint) : null} />
          </div>
        </div>
        <div style={cardStyle}>
          <div style={labelStyle}>{r.confirmedRevenue}</div>
          <div style={valueStyle}><Money value={s.confirmed_revenue > 0 ? s.confirmed_revenue : s.estimated_midpoint} lang={language} estimate={s.confirmed_revenue === 0} />
            <Delta current={s.confirmed_revenue || s.estimated_midpoint} previous={prior ? (prior.confirmed_revenue || prior.estimated_midpoint) : null} />
          </div>
        </div>
        <div style={cardStyle}>
          <div style={labelStyle}>{r.depositsCollected}</div>
          <div style={valueStyle}><Money value={s.deposits_collected} lang={language} /></div>
          {s.deposit_count > 0 && <div style={{ ...labelStyle, marginTop: '0.25rem' }}>{s.deposit_count} {r.deposits}</div>}
        </div>
        <div style={cardStyle}>
          <div style={labelStyle}>{r.refundsIssued}</div>
          <div style={{ ...valueStyle, color: s.refunds_issued > 0 ? '#EF4444' : '#E8ECF0' }}><Money value={s.refunds_issued} lang={language} /></div>
        </div>
        <div style={cardStyle}>
          <div style={labelStyle}>{r.avgTicketConfirmed}</div>
          <div style={valueStyle}><Money value={s.avg_ticket_confirmed || s.avg_ticket_estimated} lang={language} estimate={!s.avg_ticket_confirmed} /></div>
        </div>
        <div style={cardStyle}>
          <div style={labelStyle}>{r.completionRate}</div>
          <div style={valueStyle}>{formatPercent(s.completion_rate)}<Delta current={s.completion_rate} previous={prior ? 100 : null} /></div>
        </div>
        <div style={cardStyle}>
          <div style={labelStyle}>{r.cancellationRate}</div>
          <div style={{ ...valueStyle, color: s.cancellation_rate > 20 ? '#EF4444' : '#E8ECF0' }}>{formatPercent(s.cancellation_rate)}</div>
        </div>
        <div style={cardStyle}>
          <div style={labelStyle}>{r.avgLeadDays}</div>
          <div style={valueStyle}>{s.avg_lead_days != null ? `${s.avg_lead_days} ${r.days}` : '—'}</div>
        </div>
        <div style={cardStyle}>
          <div style={labelStyle}>{r.repeatRate}</div>
          <div style={valueStyle}>{formatPercent(s.repeat_rate)}</div>
          <div style={{ ...labelStyle, marginTop: '0.25rem' }}>{s.repeat_customers}/{s.unique_customers} {r.customers}</div>
        </div>
        {s.no_show_count > 0 && (
          <div style={cardStyle}>
            <div style={labelStyle}>{r.noShows}</div>
            <div style={{ ...valueStyle, color: '#F97316' }}>{s.no_show_count}</div>
          </div>
        )}
        <div style={cardStyle}>
          <div style={labelStyle}>{r.avgDaysBetweenVisits}</div>
          <div style={valueStyle}>{s.avg_days_between_visits != null ? `${s.avg_days_between_visits} ${r.days}` : '—'}</div>
        </div>
      </div>

      {/* ── Charts ────────────────────────────────────────────── */}
      {monthChartData.length > 1 && (
        <>
          <h6 style={headStyle}>{r.revenueOverTime}</h6>
          <div style={{ ...cardStyle, padding: '1rem', textAlign: 'left', marginBottom: '1rem' }}>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={monthChartData}>
                <defs>
                  <linearGradient id="colorEst" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.15}/>
                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.1)" />
                <XAxis dataKey="month" tick={{ fill: '#94A3B8', fontSize: 11 }} axisLine={false} />
                <YAxis tick={{ fill: '#94A3B8', fontSize: 11 }} axisLine={false} tickFormatter={v => formatCurrency(v, language)} />
                <Tooltip contentStyle={{ background: '#0C1220', border: '1px solid rgba(148,163,184,0.2)', color: '#E8ECF0', fontSize: '0.8rem' }}
                  formatter={(v, name) => [formatCurrency(v, language), name === 'confirmed' ? r.confirmed : r.estimatedMid]} />
                <Area type="monotone" dataKey="estimatedMid" stroke="#3B82F6" fill="url(#colorEst)" strokeWidth={1.5} name={r.estimatedMid} />
                <Area type="monotone" dataKey="confirmed" stroke="#22C55E" fill="none" strokeWidth={2} name={r.confirmed} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      {top5Services.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: statusChartData.length > 1 ? '1fr 1fr' : '1fr', gap: '1rem', marginBottom: '1rem' }}>
          <div>
            <h6 style={headStyle}>{r.topServices}</h6>
            <div style={{ ...cardStyle, padding: '1rem', textAlign: 'left' }}>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={top5Services} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.1)" />
                  <XAxis type="number" tick={{ fill: '#94A3B8', fontSize: 10 }} tickFormatter={v => formatCurrency(v, language)} />
                  <YAxis type="category" dataKey="name" tick={{ fill: '#E8ECF0', fontSize: 11 }} width={130} />
                  <Tooltip contentStyle={{ background: '#0C1220', border: '1px solid rgba(148,163,184,0.2)', color: '#E8ECF0', fontSize: '0.8rem' }}
                    formatter={(v, _n, entry) => [formatCurrency(v, language), entry.payload.isEstimate ? r.estimatedMid : r.confirmed]} />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                    {top5Services.map((entry, i) => (
                      <Cell key={i} fill={entry.isEstimate ? '#3B82F6' : '#22C55E'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {statusChartData.length > 1 && (
            <div>
              <h6 style={headStyle}>{r.statusDistribution}</h6>
              <div style={{ ...cardStyle, padding: '1rem', textAlign: 'left' }}>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={statusChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.1)" />
                    <XAxis dataKey="month" tick={{ fill: '#94A3B8', fontSize: 10 }} />
                    <YAxis tick={{ fill: '#94A3B8', fontSize: 10 }} />
                    <Tooltip contentStyle={{ background: '#0C1220', border: '1px solid rgba(148,163,184,0.2)', color: '#E8ECF0', fontSize: '0.8rem' }} />
                    <Legend wrapperStyle={{ fontSize: '0.7rem', color: '#94A3B8' }} />
                    {['completed', 'confirmed', 'in_progress', 'pending', 'cancelled'].map(status => (
                      <Bar key={status} dataKey={status} stackId="a" fill={STATUS_COLORS[status]} name={t.dashboard?.status?.[status] || status} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Revenue by Service ────────────────────────────────── */}
      <h6 style={headStyle}>
        {r.byService}
        <span style={{ float: 'right', fontSize: '0.65rem', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
          {r.sortBy}:
          {['revenue', 'count', 'name'].map(key => (
            <button key={key} onClick={() => setSvcSort(key)}
              style={{ background: 'none', border: 'none', color: svcSort === key ? '#D42B2B' : '#94A3B8', cursor: 'pointer', fontSize: '0.65rem', marginLeft: '0.5rem', textDecoration: svcSort === key ? 'underline' : 'none' }}>
              {r[`sort_${key}`]}
            </button>
          ))}
        </span>
      </h6>
      {sortedServices.length === 0 ? (
        <div style={{ color: '#94A3B8', fontSize: '0.85rem' }}>{r.noData}</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <caption className="visually-hidden">{r.byServiceCaption}</caption>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(148,163,184,0.2)' }}>
                <th scope="col" style={{ ...thStyle, textAlign: 'left' }}>{r.service}</th>
                <th scope="col" style={{ ...thStyle, textAlign: 'center' }}>{r.lineItems}</th>
                <th scope="col" style={{ ...thStyle, textAlign: 'center' }}>{r.appointments}</th>
                <th scope="col" style={{ ...thStyle, textAlign: 'right' }}>{r.confirmed}</th>
                <th scope="col" style={{ ...thStyle, textAlign: 'right' }}>{r.avgPrice}</th>
                <th scope="col" style={{ ...thStyle, textAlign: 'right' }}>{r.estimatedMid}</th>
                <th scope="col" style={{ ...thStyle, textAlign: 'right' }}>{r.pctOfTotal}</th>
              </tr>
            </thead>
            <tbody>
              {sortedServices.map((row, i) => {
                const avg = row.confirmed > 0 && row.line_items > 0 ? row.confirmed / row.line_items : null;
                const pct = totalMid > 0 ? (row.subtotal_mid / totalMid) * 100 : 0;
                return (
                  <tr key={i} style={rowBorder}>
                    <td style={{ ...tdStyle }}>{row.service}</td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>{row.line_items}</td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>{row.appointments}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}><Money value={row.confirmed > 0 ? row.confirmed : null} lang={language} /></td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}><Money value={avg} lang={language} estimate={!avg} /></td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: '#64748B' }}>{formatCurrency(row.subtotal_mid, language, { estimate: true })}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{formatPercent(pct)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Upcoming Revenue ──────────────────────────────────── */}
      {upcoming && upcoming.upcoming_appointments > 0 && (
        <>
          <h6 style={headStyle}>{r.upcomingRevenue}</h6>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
            <div style={cardStyle}>
              <div style={labelStyle}>{r.upcomingAppointments}</div>
              <div style={{ ...valueStyle, color: '#3B82F6' }}>{upcoming.upcoming_appointments}</div>
            </div>
            <div style={cardStyle}>
              <div style={labelStyle}>{r.estimatedLow}</div>
              <div style={{ ...valueStyle, color: '#3B82F6' }}>{formatCurrency(upcoming.revenue_low, language, { estimate: true })}</div>
            </div>
            <div style={cardStyle}>
              <div style={labelStyle}>{r.estimatedHigh}</div>
              <div style={{ ...valueStyle, color: '#3B82F6' }}>{formatCurrency(upcoming.revenue_high, language, { estimate: true })}</div>
            </div>
          </div>
          {upcomingByService && upcomingByService.length > 0 && (
            <div style={{ overflowX: 'auto', marginBottom: '1rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <caption className="visually-hidden">{r.upcomingByServiceCaption}</caption>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(148,163,184,0.2)' }}>
                    <th scope="col" style={{ ...thStyle, textAlign: 'left' }}>{r.service}</th>
                    <th scope="col" style={{ ...thStyle, textAlign: 'center' }}>{r.count}</th>
                    <th scope="col" style={{ ...thStyle, textAlign: 'right' }}>{r.estimatedRange}</th>
                  </tr>
                </thead>
                <tbody>
                  {upcomingByService.map((row, i) => (
                    <tr key={i} style={rowBorder}>
                      <td style={tdStyle}>{row.service}</td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>{row.count}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: '#3B82F6' }}>
                        {formatCurrency(row.subtotal_low, language, { estimate: true })} – {formatCurrency(row.subtotal_high, language, { estimate: true })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── Revenue by Month ──────────────────────────────────── */}
      <h6 style={headStyle}>{r.byMonth}</h6>
      {byMonth.length === 0 ? (
        <div style={{ color: '#94A3B8', fontSize: '0.85rem' }}>{r.noData}</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <caption className="visually-hidden">{r.byMonthCaption}</caption>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(148,163,184,0.2)' }}>
                <th scope="col" style={{ ...thStyle, textAlign: 'left' }}>{r.month}</th>
                <th scope="col" style={{ ...thStyle, textAlign: 'center' }}>{r.appointments}</th>
                <th scope="col" style={{ ...thStyle, textAlign: 'right' }}>{r.confirmed}</th>
                <th scope="col" style={{ ...thStyle, textAlign: 'right' }}>{r.estimatedMid}</th>
                <th scope="col" style={{ ...thStyle, textAlign: 'right' }}>{r.pctOfTotal}</th>
                <th scope="col" style={{ ...thStyle, textAlign: 'right' }}>{r.vsPrior}</th>
              </tr>
            </thead>
            <tbody>
              {byMonth.map((row, i) => {
                const prev = byMonth[i + 1]; // next in array = prior month (descending)
                const pct = monthlyTotal.revenue_mid > 0 ? (row.revenue_mid / monthlyTotal.revenue_mid) * 100 : 0;
                const delta = prev ? ((row.revenue_mid - prev.revenue_mid) / (prev.revenue_mid || 1)) * 100 : null;
                return (
                  <tr key={i} style={rowBorder}>
                    <td style={tdStyle}>{formatMonth(row.month, language)}</td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>{row.appointments}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}><Money value={row.confirmed > 0 ? row.confirmed : null} lang={language} /></td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: '#64748B' }}>{formatCurrency(row.revenue_mid, language, { estimate: true })}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{formatPercent(pct)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      {delta != null ? (
                        <span style={{ color: delta >= 0 ? '#22C55E' : '#EF4444', fontSize: '0.8rem' }}>
                          {delta >= 0 ? '\u25B2' : '\u25BC'} {Math.abs(delta).toFixed(1)}%
                        </span>
                      ) : '—'}
                    </td>
                  </tr>
                );
              })}
              {/* Totals row */}
              <tr style={{ borderTop: '2px solid rgba(148,163,184,0.3)' }}>
                <td style={{ ...tdStyle, fontWeight: 700 }}>{r.total}</td>
                <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 700 }}>{monthlyTotal.appointments}</td>
                <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700 }}><Money value={monthlyTotal.confirmed > 0 ? monthlyTotal.confirmed : null} lang={language} /></td>
                <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: '#64748B' }}>{formatCurrency(monthlyTotal.revenue_mid, language, { estimate: true })}</td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>100%</td>
                <td style={tdStyle}></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* ── Busiest Times ─────────────────────────────────────── */}
      {busiestTimes.length > 0 && (
        <>
          <h6 style={headStyle}>{r.busiestTimes}</h6>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <caption className="visually-hidden">{r.busiestTimesCaption}</caption>
              <thead>
                <tr>
                  <th scope="col" style={{ ...thStyle, textAlign: 'left' }}></th>
                  {heatmapHours.map(h => (
                    <th key={h} scope="col" style={{ ...thStyle, textAlign: 'center', padding: '0.3rem', minWidth: '36px' }}>
                      {h}h
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[1, 2, 3, 4, 5, 6, 0].map(dow => (
                  <tr key={dow}>
                    <td style={{ ...tdStyle, fontSize: '0.75rem', padding: '0.3rem 0.5rem' }}>{dayLabels[dow]}</td>
                    {heatmapHours.map(h => {
                      const count = heatmapData[`${dow}-${h}`] || 0;
                      const intensity = count / heatmapMax;
                      return (
                        <td key={h} style={{
                          textAlign: 'center', padding: '0.3rem', fontSize: '0.7rem',
                          background: count > 0 ? `rgba(212,43,43,${0.1 + intensity * 0.7})` : 'transparent',
                          color: intensity > 0.5 ? '#FFFFFF' : '#94A3B8'
                        }}>
                          {count > 0 ? count : ''}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── Top Customers ─────────────────────────────────────── */}
      {topCustomers && topCustomers.length > 0 && (
        <>
          <h6 style={headStyle}>{r.topCustomersTitle}</h6>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <caption className="visually-hidden">{r.topCustomersCaption}</caption>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(148,163,184,0.2)' }}>
                  <th scope="col" style={{ ...thStyle, textAlign: 'left' }}>{r.customer}</th>
                  <th scope="col" style={{ ...thStyle, textAlign: 'center' }}>{r.appointments}</th>
                  <th scope="col" style={{ ...thStyle, textAlign: 'right' }}>{r.revenue}</th>
                  <th scope="col" style={{ ...thStyle, textAlign: 'right' }}>{r.lastVisit}</th>
                </tr>
              </thead>
              <tbody>
                {topCustomers.map((c, i) => (
                  <tr key={i} style={rowBorder}>
                    <td style={tdStyle}>
                      <span style={{ color: '#E8ECF0', cursor: 'default' }}>
                        {c.first_name} {c.last_name}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>{c.appointment_count}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <Money value={c.confirmed_revenue > 0 ? c.confirmed_revenue : c.estimated_revenue} lang={language} estimate={c.confirmed_revenue === 0} />
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: '#94A3B8' }}>
                      {c.last_visit ? new Date(String(c.last_visit).slice(0, 10) + 'T12:00:00').toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA') : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── Top Vehicles Serviced ─────────────────────────────── */}
      {topVehicles && topVehicles.length > 0 && (
        <>
          <h6 style={headStyle}>{r.topVehiclesTitle}</h6>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <caption className="visually-hidden">{r.topVehiclesCaption}</caption>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(148,163,184,0.2)' }}>
                  <th scope="col" style={{ ...thStyle, textAlign: 'left' }}>{r.vehicle}</th>
                  <th scope="col" style={{ ...thStyle, textAlign: 'center' }}>{r.appointments}</th>
                  <th scope="col" style={{ ...thStyle, textAlign: 'right' }}>{r.revenue}</th>
                </tr>
              </thead>
              <tbody>
                {topVehicles.map((v, i) => (
                  <tr key={i} style={rowBorder}>
                    <td style={tdStyle}>{v.make} {v.model}</td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>{v.appointment_count}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <Money value={v.confirmed_revenue > 0 ? v.confirmed_revenue : v.estimated_revenue} lang={language} estimate={v.confirmed_revenue === 0} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── Revenue per Hour (Profitability) ──────────────────── */}
      {revenuePerHour && revenuePerHour.length > 0 && (
        <>
          <h6 style={headStyle}>{r.revenuePerHourTitle}</h6>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <caption className="visually-hidden">{r.revenuePerHourCaption}</caption>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(148,163,184,0.2)' }}>
                  <th scope="col" style={{ ...thStyle, textAlign: 'left' }}>{r.service}</th>
                  <th scope="col" style={{ ...thStyle, textAlign: 'center' }}>{r.duration}</th>
                  <th scope="col" style={{ ...thStyle, textAlign: 'center' }}>{r.lineItems}</th>
                  <th scope="col" style={{ ...thStyle, textAlign: 'right' }}>{r.revPerHour}</th>
                </tr>
              </thead>
              <tbody>
                {revenuePerHour.map((row, i) => {
                  const best = revenuePerHour[0]?.revenue_per_hour || 1;
                  const intensity = row.revenue_per_hour ? row.revenue_per_hour / best : 0;
                  return (
                    <tr key={i} style={rowBorder}>
                      <td style={tdStyle}>{row.service}</td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>{row.duration_minutes > 0 ? `${row.duration_minutes} min` : '—'}</td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>{row.line_items}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: intensity > 0.7 ? '#22C55E' : intensity > 0.4 ? '#EAB308' : '#94A3B8' }}>
                        {row.revenue_per_hour != null ? formatCurrency(row.revenue_per_hour, language) + '/h' : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── Customer Acquisition (New vs Returning) ───────────── */}
      {customerAcquisition && customerAcquisition.length > 0 && (
        <>
          <h6 style={headStyle}>{r.customerAcquisitionTitle}</h6>
          <div style={{ ...cardStyle, padding: '1rem', textAlign: 'left', marginBottom: '1rem' }}>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={[...customerAcquisition].reverse()}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.1)" />
                <XAxis dataKey="month" tick={{ fill: '#94A3B8', fontSize: 10 }} tickFormatter={v => formatMonth(v, language)} />
                <YAxis tick={{ fill: '#94A3B8', fontSize: 10 }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: '#0C1220', border: '1px solid rgba(148,163,184,0.2)', color: '#E8ECF0', fontSize: '0.8rem' }}
                  labelFormatter={v => formatMonth(v, language)} />
                <Legend wrapperStyle={{ fontSize: '0.7rem', color: '#94A3B8' }} />
                <Bar dataKey="new_customers" fill="#3B82F6" name={r.newCustomers} radius={[2, 2, 0, 0]} />
                <Bar dataKey="returning_customers" fill="#22C55E" name={r.returningCustomers} radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
}
